// Staff dashboard: who talked, what was said, how many minutes, what it cost,
// and every PDF we have shared from this portal. Pure DOM, no innerHTML.

export const VOICES = ["Kore", "Aoede", "Leda", "Autonoe", "Callirrhoe", "Charon", "Puck", "Fenrir", "Orus", "Iapetus"];
export const KINDS = ["tutor", "interviewer"];

export const kindLabel = (kind) => (kind === "interviewer" ? "Interviewer" : "Tutor");
export const personaFor = (kind) => (kind === "interviewer" ? "Mahendra's Interview Expert" : "Mahendra's Tutor");

/** Keep the owner's own wording; only swap a name he never changed. */
export function suggestPersona(persona, newKind, oldKind) {
  const now = String(persona || "").trim();
  return !now || now === personaFor(oldKind) ? personaFor(newKind) : now;
}

export function sessionsQuery({ days = 30, slug = "", q = "" } = {}) {
  const p = new URLSearchParams({ days: String(days) });
  if (slug) p.set("slug", slug);
  if (String(q).trim()) p.set("q", String(q).trim());
  return `/api/admin/sessions?${p.toString()}`;
}
export const leadsUrl = (days = 30) => `/api/admin/sessions.csv?days=${encodeURIComponent(days)}`;
export const recordUrl = (sid) => `/api/admin/sessions/${encodeURIComponent(sid)}/record.pdf`;

/** Only the fields the owner actually changed, so we never blank a title. */
export function editPatch(original = {}, next = {}) {
  const body = {};
  for (const field of ["title", "kind", "persona", "voice"]) {
    const value = typeof next[field] === "string" ? next[field].trim() : next[field];
    if (value === undefined || value === null || value === "") continue;
    if (value !== (original[field] ?? "")) body[field] = value;
  }
  return body;
}

/** The PDF list plus the numbers for the chosen period, by slug, either way round. */
export function mergePdfs(pdfs = [], stats = []) {
  const by = new Map(stats.map((s) => [s.slug, s]));
  const rows = pdfs.map((p) => {
    const s = by.get(p.slug) || {};
    return {
      ...p,
      title: p.title || s.title || p.slug,
      kind: p.kind || s.kind || "tutor",
      persona: p.persona || s.persona || "",
      voice: p.voice || s.voice || "",
      pages: p.pages ?? s.pages ?? 0,
      url: p.url || s.url || "",
      sessions: s.sessions ?? p.sessions ?? 0,
      students: s.students || 0,
      minutes: s.minutes || 0,
      inr: s.inr || 0,
    };
  });
  const seen = new Set(rows.map((r) => r.slug));
  for (const s of stats) if (!seen.has(s.slug)) rows.push({ ...s, kind: s.kind || "tutor" });
  return rows;
}

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

export function mmss(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function whenText(iso) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return "Not known";
  const day = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `${day}, ${d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}`;
}

// ---------- tiny DOM helpers (the page never builds HTML from data) ----------

function el(doc, tag, attrs = {}, kids = []) {
  const node = doc.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "class") node.className = v;
    else if (k === "text") node.textContent = String(v);
    else if (k === "data") for (const [dk, dv] of Object.entries(v)) node.dataset[dk] = String(dv);
    else if (k === "on") for (const [ev, fn] of Object.entries(v)) node.addEventListener(ev, fn);
    else if (k.includes("-")) node.setAttribute(k, String(v));
    else node[k] = v;
  }
  for (const kid of kids) if (kid) node.appendChild(kid);
  return node;
}

const given = (doc, value) => {
  const v = String(value || "").trim();
  return v ? el(doc, "span", { text: v }) : el(doc, "span", { class: "muted", text: "Not given" });
};

function mobileCell(doc, mobile) {
  const v = String(mobile || "").trim();
  if (!v) return el(doc, "span", { class: "muted", text: "Not given" });
  return el(doc, "a", { href: `tel:${v}`, text: v, on: { click: (e) => e.stopPropagation?.() } });
}

function select(doc, id, values, chosen, label) {
  const sel = el(
    doc,
    "select",
    { id },
    values.map((v) => el(doc, "option", { value: v, text: label ? label(v) : v, selected: v === chosen })),
  );
  sel.value = chosen;
  return sel;
}

/** One short message in place of a section, so a failed call never blanks the page. */
export function message(box, text) {
  const doc = box.ownerDocument;
  if (box.tagName === "TBODY") return box.replaceChildren(el(doc, "tr", {}, [el(doc, "td", { class: "muted", colSpan: 9, text })]));
  const tag = box.tagName === "UL" || box.tagName === "OL" ? "li" : "p";
  box.replaceChildren(el(doc, tag, { class: "muted", text }));
}

// ---------- totals ----------

export function renderTotals(box, totals = {}) {
  const doc = box.ownerDocument;
  const tiles = [
    ["Conversations", String(totals.sessions ?? 0)],
    ["Students", String(totals.students ?? 0)],
    ["Minutes talked", num(totals.minutes)],
    ["API cost", inr(totals.inr)],
    ["Leads (gave a mobile)", String(totals.leads ?? 0)],
  ];
  box.replaceChildren(...tiles.map(([label, value]) => el(doc, "div", { class: "tile" }, [el(doc, "b", { text: value }), el(doc, "span", { text: label })])));
}

// ---------- sessions table ----------

export function renderSessions(tbody, sessions = [], onPick) {
  const doc = tbody.ownerDocument;
  if (!sessions.length) return message(tbody, "Nobody talked in this period.");
  tbody.replaceChildren(
    ...sessions.map((s) => {
      const st = s.student || {};
      const row = el(doc, "tr", { class: "srow", tabIndex: 0, data: { sid: s.sid } }, [
        el(doc, "td", { text: whenText(s.startedAt) }),
        el(doc, "td", {}, [given(doc, st.name)]),
        el(doc, "td", {}, [mobileCell(doc, st.mobile)]),
        el(doc, "td", {}, [given(doc, st.exam)]),
        el(doc, "td", { text: s.title || s.slug || "" }),
        el(doc, "td", {}, [el(doc, "span", { class: s.kind === "interviewer" ? "chip ask" : "chip", text: kindLabel(s.kind) })]),
        el(doc, "td", { text: s.endedAt ? s.length || num(s.minutes) : "talking now" }),
        el(doc, "td", { text: String(s.turns ?? 0) }),
        el(doc, "td", { text: s.unanswered ? String(s.unanswered) : "–" }),
        el(doc, "td", {}, [
          s.score == null
            ? el(doc, "span", { class: "muted", text: "–" })
            : el(doc, "span", { class: "chip ask", text: `${s.score}/100` }),
        ]),
        el(doc, "td", {}, [
          s.stars ? el(doc, "span", { class: "chip", text: `${s.stars}/5` }) : el(doc, "span", { class: "muted", text: "–" }),
        ]),
        el(doc, "td", { text: inr(s.inr) }),
      ]);
      if (onPick) {
        row.addEventListener("click", () => onPick(s.sid));
        row.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") onPick(s.sid);
        });
      }
      return row;
    }),
  );
}

// ---------- one conversation ----------

function facts(doc, d) {
  const st = d.student || {};
  const pairs = [
    ["Student", String(st.name || "").trim() || "Not given"],
    ["Mobile", String(st.mobile || "").trim() || "Not given"],
    ["Exam", String(st.exam || "").trim() || "Not given"],
    ["PDF", d.title || d.slug || ""],
    ["Kind", `${kindLabel(d.kind)} · ${d.persona || personaFor(d.kind)}`],
    ["Started", whenText(d.startedAt)],
    ["How long we talked", d.endedAt ? d.length || `${num(d.minutes)} min` : `${d.length || num(d.minutes)} (still talking)`],
    ["Could not answer", String((d.unanswered || []).length || 0)],
    ["Score", d.score ? `${d.score.outOf100} out of 100${d.score.verdict ? ` · ${d.score.verdict}` : ""}` : "Not scored"],
    ["Phone or computer", [d.deviceType, d.device && String(d.device).slice(0, 6)].filter(Boolean).join(" · ") || "Not known"],
    ["Cost", `${inr(d.inr)}${d.pdfs ? ` (talk + ${d.pdfs} PDF${d.pdfs > 1 ? "s" : ""})` : ""}`],
    ["Sound broke up", d.gaps ? `${d.gaps} time${d.gaps > 1 ? "s" : ""}` : "No"],
    ["What the student thought", d.feedback?.stars ? `${"★".repeat(d.feedback.stars)}${"☆".repeat(5 - d.feedback.stars)} ${d.feedback.stars} of 5` : "Not asked"],
  ];
  return pairs.map(([k, v]) => el(doc, "div", {}, [el(doc, "dt", { text: k }), el(doc, "dd", { text: v })]));
}

export function renderTranscript(box, lines = [], aiName = "Tutor") {
  const doc = box.ownerDocument;
  if (!lines.length) return message(box, "Nothing was recorded for this conversation.");
  box.replaceChildren(
    ...lines.map((ln) => {
      const student = ln.role === "student";
      return el(doc, "li", { class: student ? "line from-student" : "line from-ai" }, [
        el(doc, "span", { class: "who", text: student ? "Student" : aiName }),
        el(doc, "p", { class: "said", text: ln.text || "" }),
        el(doc, "time", { class: "at", text: mmss(ln.t) }),
      ]);
    }),
  );
}

export function renderDetail(box, d, { onBack, onRecord } = {}) {
  const doc = box.ownerDocument;
  const log = el(doc, "ul", { class: "talk-log" });
  renderTranscript(log, d.lines, d.persona || kindLabel(d.kind));
  const note = el(doc, "p", { class: "rule", role: "alert" });
  box.replaceChildren(
    el(doc, "div", { class: "row det-head" }, [
      el(doc, "h3", { class: "det-title", text: d.title || d.slug || "Conversation" }),
      el(doc, "div", { class: "actions" }, [
        el(doc, "button", {
          class: "btn soft small",
          text: "Download record (PDF)",
          on: {
            click: () =>
              Promise.resolve(onRecord?.(d)).catch((e) => {
                note.textContent = `Could not download the record: ${e.message}`;
              }),
          },
        }),
        el(doc, "button", { class: "btn plain small", text: "Back to list", on: { click: () => onBack?.() } }),
      ]),
    ]),
    el(doc, "dl", { class: "facts" }, facts(doc, d)),
    note,
    ...scoreBlock(doc, d.score),
    ...feedbackBlock(doc, d.feedback),
    ...reviewBlock(doc, d.review),
    ...unansweredBlock(doc, d.unanswered),
    el(doc, "h4", { class: "usage-h", text: "What was said" }),
    log,
  );
}

/** How the score was reached, area by area, so a mark can always be explained to a student. */
function scoreBlock(doc, score) {
  if (!score || typeof score.outOf100 !== "number") return [];
  const areas = Array.isArray(score.areas) ? score.areas : [];
  const rows = areas.map((a) =>
    el(doc, "tr", {}, [
      el(doc, "td", { text: a.area || "" }),
      el(doc, "td", { text: `${a.marks} of ${a.outOf}` }),
      el(doc, "td", { class: "muted", text: a.why || "" }),
    ]),
  );
  return [
    el(doc, "h4", { class: "usage-h", text: `Score: ${score.outOf100} out of 100${score.verdict ? ` · ${score.verdict}` : ""}` }),
    ...(rows.length
      ? [
          el(doc, "table", { class: "usage" }, [
            el(doc, "thead", {}, [
              el(doc, "tr", {}, [el(doc, "th", { text: "Area" }), el(doc, "th", { text: "Marks" }), el(doc, "th", { text: "Why" })]),
            ]),
            el(doc, "tbody", {}, rows),
          ]),
        ]
      : []),
    ...(score.strengths ? [el(doc, "p", { class: "small", text: `What went well: ${score.strengths}` })] : []),
    ...(score.weaknesses ? [el(doc, "p", { class: "small", text: `What to work on: ${score.weaknesses}` })] : []),
    ...(score.improve?.length
      ? [
          el(doc, "p", { class: "small", text: "How to raise this score:" }),
          el(doc, "ul", { class: "steps" }, score.improve.map((x) => el(doc, "li", { class: "small", text: x }))),
        ]
      : []),
  ];
}

/** What the student said about us: the stars and their own words. */
function feedbackBlock(doc, fb) {
  if (!fb || (!fb.stars && !fb.remarks)) return [];
  return [
    el(doc, "h4", { class: "usage-h", text: `Student's feedback${fb.stars ? `: ${fb.stars} of 5` : ""}` }),
    ...(fb.remarks ? [el(doc, "p", { class: "small", text: fb.remarks })] : []),
  ];
}

/** Every mock question: what the student said, and the answer Mahendra's suggests in its place. */
function reviewBlock(doc, list) {
  if (!Array.isArray(list) || !list.length) return [];
  return [
    el(doc, "h4", { class: "usage-h", text: `Question by question, with Mahendra's suggestion (${list.length})` }),
    el(
      doc,
      "ul",
      { class: "unanswered" },
      list.map((q, i) =>
        el(doc, "li", { class: "stack" }, [
          el(doc, "span", {}, [
            el(doc, "time", { class: "at", text: mmss(q.t) }),
            el(doc, "span", { class: "said", text: ` Q${i + 1}. ${q.question || ""}` }),
          ]),
          el(doc, "span", { class: "muted small", text: `They said: ${q.said || "nothing"}` }),
          ...(q.better ? [el(doc, "span", { class: "small", text: `Mahendra's suggestive answer: ${q.better}` })] : []),
        ]),
      ),
    ),
  ];
}

/** Questions the tutor could not answer from this PDF: what the material is missing. */
function unansweredBlock(doc, list) {
  if (!Array.isArray(list) || !list.length) return [];
  return [
    el(doc, "h4", { class: "usage-h", text: "Questions we could not answer" }),
    el(
      doc,
      "ul",
      { class: "unanswered" },
      list.map((q) =>
        el(doc, "li", { class: "stack" }, [
          el(doc, "span", {}, [
            el(doc, "time", { class: "at", text: mmss(q.t) }),
            el(doc, "span", { class: "said", text: ` ${q.question || ""}` }),
            el(doc, "span", { class: "muted small", text: q.kind === "partly_answered" ? " · partly answered" : " · not answered" }),
          ]),
          ...(q.missing ? [el(doc, "span", { class: "muted small", text: `Missing: ${q.missing}` })] : []),
          ...(q.expert ? [el(doc, "span", { class: "small", text: `Mahendra's answer: ${q.expert}` })] : []),
          ...(!q.expert && q.why ? [el(doc, "span", { class: "muted small", text: q.why })] : []),
        ]),
      ),
    ),
  ];
}

export function renderError(box, text, onBack) {
  const doc = box.ownerDocument;
  box.replaceChildren(el(doc, "p", { class: "muted", text }), el(doc, "button", { class: "btn plain small", text: "Back to list", on: { click: () => onBack?.() } }));
}

// ---------- PDFs ----------

export function fillPdfFilter(sel, rows = [], chosen = "") {
  const doc = sel.ownerDocument;
  sel.replaceChildren(
    el(doc, "option", { value: "", text: "All PDFs" }),
    ...rows.map((r) => el(doc, "option", { value: r.slug, text: r.title || r.slug, selected: r.slug === chosen })),
  );
  sel.value = chosen;
}

function editForm(doc, r, hooks) {
  const title = el(doc, "input", { class: "field", value: r.title || "", id: `e-title-${r.slug}` });
  const persona = el(doc, "input", { class: "field", value: r.persona || "", placeholder: personaFor(r.kind), id: `e-persona-${r.slug}` });
  const kind = select(doc, `e-kind-${r.slug}`, KINDS, r.kind, kindLabel);
  const voice = select(doc, `e-voice-${r.slug}`, VOICES, r.voice || VOICES[0]);
  const hint = el(doc, "p", { class: "rule", text: `Suggested name for a ${kindLabel(r.kind).toLowerCase()} PDF: ${personaFor(r.kind)}` });
  const note = el(doc, "p", { class: "rule", role: "alert" });
  let last = r.kind;
  kind.addEventListener("change", () => {
    persona.value = suggestPersona(persona.value, kind.value, last);
    persona.placeholder = personaFor(kind.value);
    hint.textContent = `Suggested name for a ${kindLabel(kind.value).toLowerCase()} PDF: ${personaFor(kind.value)}`;
    last = kind.value;
  });
  const save = el(doc, "button", {
    class: "btn small",
    text: "Save",
    on: {
      click: () => {
        const body = editPatch(r, { title: title.value, kind: kind.value, persona: persona.value, voice: voice.value });
        if (!Object.keys(body).length) return hooks.cancel?.();
        save.disabled = true;
        note.textContent = "Saving…";
        Promise.resolve(hooks.save?.(r.slug, body)).catch((e) => {
          save.disabled = false;
          note.textContent = `Could not save: ${e.message}`;
        });
      },
    },
  });
  // a new version of the same PDF: the link, the QR and every printed button keep working
  const file = el(doc, "input", { class: "field", type: "file", accept: "application/pdf,.pdf", id: `e-file-${r.slug}` });
  const swap = el(doc, "button", {
    class: "btn plain small",
    text: "Replace the file",
    on: {
      click: () => {
        const chosen = file.files && file.files[0];
        if (!chosen) {
          note.textContent = "Choose the new PDF first.";
          return;
        }
        if (!confirm(`Replace the file behind “${r.title}” with ${chosen.name}? The student link stays the same.`)) return;
        swap.disabled = true;
        note.textContent = "Reading the new file…";
        const form = new FormData();
        form.append("file", chosen);
        Promise.resolve(hooks.replace?.(r.slug, form)).catch((e) => {
          swap.disabled = false;
          note.textContent = `Could not replace it: ${e.message}`;
        });
      },
    },
  });
  return el(doc, "div", { class: "edit" }, [
    el(doc, "div", { class: "row" }, [
      el(doc, "label", { text: "Title students see", htmlFor: title.id }, [title]),
      el(doc, "label", { text: "Kind", htmlFor: kind.id }, [kind]),
    ]),
    el(doc, "div", { class: "row" }, [
      el(doc, "label", { text: "Name the AI uses", htmlFor: persona.id }, [persona]),
      el(doc, "label", { text: "Voice", htmlFor: voice.id }, [voice]),
    ]),
    hint,
    el(doc, "div", { class: "row" }, [
      el(doc, "label", { text: `New version of this PDF (now ${r.pages || "?"} pages) — same link`, htmlFor: file.id }, [file]),
      swap,
    ]),
    note,
    el(doc, "div", { class: "row" }, [save, el(doc, "button", { class: "btn plain small", text: "Cancel", on: { click: () => hooks.cancel?.() } })]),
  ]);
}

export function renderPdfs(box, rows = [], hooks = {}) {
  const doc = box.ownerDocument;
  if (!rows.length) return message(box, "No PDFs yet. Add one below.");
  box.replaceChildren(
    ...rows.map((r) => {
      const meta = [
        kindLabel(r.kind),
        r.persona || personaFor(r.kind),
        `${r.pages || 0} pages`,
        `${r.students || 0} people`,
        `${num(r.minutes)} minutes`,
        inr(r.inr),
      ].join(" · ");
      const copy = el(doc, "button", { class: "btn plain small", text: "Copy link" });
      copy.addEventListener("click", () => {
        const done = (word) => {
          copy.textContent = word;
          setTimeout(() => (copy.textContent = "Copy link"), 1800);
        };
        Promise.resolve(hooks.copy?.(r.url)).then(
          () => done("Copied"),
          () => done("Could not copy"),
        );
      });
      const card = el(doc, "article", { class: "card item", data: { slug: r.slug } }, [
        el(doc, "div", {}, [el(doc, "h3", { text: r.title || r.slug }), el(doc, "div", { class: "meta", text: meta })]),
        el(doc, "div", { class: "actions" }, [
          r.url && el(doc, "a", { class: "btn small", href: r.url, text: "Open", target: "_blank", rel: "noopener" }),
          el(doc, "a", {
            class: "btn soft small",
            href: `/api/pdfs/${encodeURIComponent(r.slug)}/tutor.pdf`,
            text: "PDF to share",
            title: "The same PDF with an Ask button on every page, to share on WhatsApp",
          }),
          el(doc, "button", { class: "btn soft small", text: "QR code", on: { click: () => hooks.qr?.(r) } }),
          el(doc, "button", { class: "btn plain small", text: "Edit", on: { click: () => hooks.edit?.(r.slug) } }),
          el(doc, "button", { class: "btn danger small", text: "Delete", on: { click: () => hooks.del?.(r) } }),
        ]),
        el(doc, "div", { class: "link" }, [el(doc, "code", { text: r.url || "" }), copy]),
      ]);
      if (hooks.editing === r.slug) card.appendChild(editForm(doc, r, hooks));
      return card;
    }),
  );
}

// ---------- wiring ----------

export function createDash({ api, download, $, copy }) {
  const state = { days: 30, slug: "", q: "", pdfs: [], stats: [], editing: "" };
  let typing = 0;
  let bound = false;

  const paintPdfs = () => {
    const rows = mergePdfs(state.pdfs, state.stats);
    renderPdfs($("list"), rows, {
      editing: state.editing,
      copy: (url) => copy(url),
      qr: (r) => hooks.qr?.(r),
      del: (r) => hooks.del?.(r),
      edit: (slug) => {
        state.editing = state.editing === slug ? "" : slug;
        paintPdfs();
      },
      cancel: () => {
        state.editing = "";
        paintPdfs();
      },
      save: async (slug, body) => {
        await api(`/api/admin/pdfs/${encodeURIComponent(slug)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        state.editing = "";
        await Promise.all([loadPdfs(), loadSessions()]);
      },
      replace: async (slug, form) => {
        const meta = await api(`/api/admin/pdfs/${encodeURIComponent(slug)}/file`, { method: "PUT", body: form });
        state.editing = "";
        await Promise.all([loadPdfs(), loadSessions()]);
        hooks.replaced?.(meta);
      },
    });
    fillPdfFilter($("fSlug"), rows, state.slug);
  };

  const hooks = {};

  async function loadPdfs() {
    try {
      state.pdfs = await api("/api/admin/pdfs");
    } catch {
      state.pdfs = [];
    }
    paintPdfs();
  }

  async function loadSessions() {
    try {
      const data = await api(sessionsQuery(state));
      state.stats = data.pdfs || [];
      renderTotals($("totals"), data.totals || {});
      $("totalsNote").textContent = `Everything below is for the last ${state.days} days.`;
      renderSessions($("sessionRows"), data.sessions, show);
      $("sessionsMsg").hidden = true;
      paintPdfs();
    } catch (e) {
      message($("sessionRows"), "Not loaded.");
      $("sessionsMsg").textContent = `Could not load the conversations: ${e.message}`;
      $("sessionsMsg").hidden = false;
    }
  }

  const back = () => {
    $("detail").hidden = true;
    $("detail").replaceChildren();
    $("browse").hidden = false;
  };

  async function show(sid) {
    const box = $("detail");
    $("browse").hidden = true;
    box.hidden = false;
    message(box, "Loading the conversation…");
    window.scrollTo?.({ top: 0 });
    try {
      const d = await api(`/api/admin/sessions/${encodeURIComponent(sid)}`);
      renderDetail(box, d, { onBack: back, onRecord: (s) => download(recordUrl(s.sid), `${String(s.student?.name || "conversation").replace(/[^\w ]+/g, "").trim() || "conversation"} record.pdf`) });
    } catch (e) {
      renderError(box, `Could not open this conversation: ${e.message}`, back);
    }
  }

  function bind() {
    if (bound) return;
    bound = true;
    $("fDays").addEventListener("change", () => {
      state.days = Number($("fDays").value) || 30;
      loadSessions();
    });
    $("fSlug").addEventListener("change", () => {
      state.slug = $("fSlug").value;
      loadSessions();
    });
    $("fQ").addEventListener("input", () => {
      clearTimeout(typing);
      typing = setTimeout(() => {
        state.q = $("fQ").value;
        loadSessions();
      }, 300);
    });
    $("leadsBtn").addEventListener("click", () => {
      Promise.resolve(download(leadsUrl(state.days), `leads-last-${state.days}-days.csv`)).catch((e) => {
        $("sessionsMsg").textContent = `Could not download the leads: ${e.message}`;
        $("sessionsMsg").hidden = false;
      });
    });
  }

  return {
    on: (name, fn) => (hooks[name] = fn),
    back,
    refresh: async () => {
      bind();
      await Promise.all([loadPdfs(), loadSessions()]);
    },
  };
}
